// Cenário por departamento: SEM a estrutura do DRE. Linhas = categorias do ERP, no máximo em dois
// níveis (categoria superior → categorias); base = realizado do DRE por departamento (regime do plano) no período de
// referência, por categoria x departamento (+ GERAL, os títulos sem departamento). O consolidado é a
// soma dos departamentos e é ele que vira o orçamento do Gestão/Studio (o backend grava a soma).
//
// Para reaproveitar o motor do simulador, a categoria superior faz o papel de "nó": o % ou o R$
// digitado nela passa para as filhas (a filha com valor próprio prevalece), como uma conta do DRE.
//
// Estado (imutável):
// {
//   mode:   'percentual' | 'valor'        forma de início, igual para todos os departamentos
//   linked: { [nodeId]: boolean }         (sem uso aqui: não há "acompanha a receita" sem o DRE)
//   byDept: { [deptKey]: adjustments }    ajustes de cada departamento, no formato de adjustments.js
// }
//
// Cada departamento é simulado com o modelo da empresa trocando só a base (a do departamento).

import { MONTH_INDEXES, zeros12 } from './periods';
import { buildBaseSeries, buildMonthSources, FINANCIAL_GROUP_KEYS, INDICATOR_KEYS, realizedByCategory } from './dataset';
import {
  adjustmentsFromApi,
  adjustmentsToApi,
  alignCategoryKeys,
  countAdjusted,
  emptyAdjustments,
  isValueMode,
  normalizeInputMode,
} from './adjustments';
import {
  computeBaseSnapshot,
  finishSimulation,
  projectedValuesToApi,
  revenueTotals,
  simulate,
  variationOfTotals,
} from './simulate';
import { applyBulk, BULK_KIND, bulkEquivalentPct, bulkTargetNodes } from './bulk';
import { goalPeriodsFor, METRIC_TYPE, metricId } from './goalSnapshot';
import { ROW_KIND } from './rows';
import { categoryItemComparator, collectErpLeaves, compareCode, parseCategoryNature } from './erpCategories';

/** "Departamento" dos lançamentos sem departamento (mesma chave do backend). */
export const GERAL_DEPT_KEY = '__GERAL__';
export const GERAL_DEPT_LABEL = 'GERAL';

export const SCENARIO_TYPE = Object.freeze({ CONSOLIDATED: 'consolidado', DEPARTMENT: 'departamento' });

export const normalizeScenarioType = (type) => (type === SCENARIO_TYPE.DEPARTMENT ? SCENARIO_TYPE.DEPARTMENT : SCENARIO_TYPE.CONSOLIDATED);

/** O Bling (ERP 6) não tem departamento: só cenário consolidado. */
export const erpSupportsDepartments = (erp) => erp == null || (Number(erp) !== 6 && String(erp).toLowerCase() !== 'bling');

export const isDepartmentScenario = (pageOrType) =>
  normalizeScenarioType(typeof pageOrType === 'string' ? pageOrType : pageOrType?.type) === SCENARIO_TYPE.DEPARTMENT;

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const hasValue = (series) => Boolean(series) && series.some((v) => Math.abs(v) > 0.005);

// ---------------- Dataset ----------------

/** Linha de resultado (soma de todas as categorias), no fim da tabela. */
export const RESULT_NODE_ID = '__RESULTADO__';
/** Subtotais das entradas e das saídas. */
export const ENTRADAS_NODE_ID = '__ENTRADAS__';
export const SAIDAS_NODE_ID = '__SAIDAS__';

const groupNodeId = (cod) => `G|${cod}`;

const sumSeries = (base, cods) => cods.reduce((acc, cod) => acc + (base.get(cod) || []).reduce((a, b) => a + b, 0), 0);

/**
 * Modelo do cenário por departamento, no formato que o motor do simulador usa, montado das
 * categorias do ERP (`raw.categoryTree`) e do DRE por departamento (`raw.values`):
 * - categorias = as de baixo (sem filhas) ativas ou com realizado; uma categoria com filhas e com
 *   título próprio também aparece como linha ("lançamentos diretos");
 * - nós = as categorias superiores de cada categoria (`ancestors`: só a direta, exceto no Conta Azul,
 *   que tem até 3 níveis e traz a cadeia inteira, um nó por nível), os subtotais de entradas e de
 *   saídas e o resultado; categoria sem superior fica solta, no primeiro nível;
 * - `departments` [{ key, label, hasBase }] (GERAL, quando há títulos sem departamento, primeiro) e
 *   `baseByDept` Map(depto -> Map(cod -> [12]));
 * - `rows`: linhas da tabela — entradas, depois saídas (cada bloco com o subtotal), e o resultado.
 *   Dentro do bloco, pelo código quando ele é estruturado ("1.01.02"), senão pelo nome.
 */
export function prepareDepartmentModel(raw, { planYear } = {}) {
  const data = raw || {};
  const values = Array.isArray(data.values) ? data.values : [];
  // Categorias (linhas editáveis) e a superior de cada uma.
  const { byCod, leaves } = collectErpLeaves(data);

  const catList = [...leaves.keys()].sort(compareCode);
  const catSet = new Set(catList);

  // Nós: as categorias superiores das linhas, e o resultado.
  const nodeById = new Map();
  const categories = new Map();
  catList.forEach((cod) => {
    const leaf = leaves.get(cod);
    const nodeIds = [];
    let parent = null;
    leaf.ancestors.forEach((groupCod) => {
      const id = groupNodeId(groupCod);
      if (!nodeById.has(id)) {
        nodeById.set(id, {
          id, codDRE: groupCod, codDRESup: parent ? parent.codDRE : null, descricao: byCod.get(groupCod)?.descricao || groupCod,
          position: 0, isTotalizador: false, isConta: true, isSubConta: false, isCusto: false, totalizadorAV: false,
          parentId: parent ? parent.id : null, childIds: [], directCats: [], allCats: [], depth: parent ? parent.depth + 1 : 0,
        });
        if (parent) parent.childIds.push(id);
      }
      parent = nodeById.get(id);
    });
    if (parent) {
      parent.directCats.push(cod);
      nodeIds.push(parent.id);
    }
    categories.set(cod, { cod, descricao: leaf.descricao, nodeIds });
  });
  // Base: realizado de cada departamento; a da empresa é a soma.
  const monthSources = buildMonthSources(data);
  const valuesByDept = new Map();
  values.forEach((v) => {
    const key = String(v?.codDepartamento ?? '').trim() || GERAL_DEPT_KEY;
    if (!valuesByDept.has(key)) valuesByDept.set(key, []);
    valuesByDept.get(key).push(v);
  });
  // GERAL só quando há títulos sem departamento (sem ele o Total não bateria com o realizado); um
  // plano salvo com valores no GERAL o traz de volta (listDepartments).
  const list = valuesByDept.has(GERAL_DEPT_KEY) ? [{ key: GERAL_DEPT_KEY, label: GERAL_DEPT_LABEL }] : [];
  const seen = new Set([GERAL_DEPT_KEY]);
  (Array.isArray(data.departments) ? data.departments : []).forEach((d) => {
    const key = String(d?.key ?? '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    list.push({ key, label: String(d.label || key) });
  });
  valuesByDept.forEach((_, key) => {
    if (seen.has(key)) return;
    seen.add(key);
    list.push({ key, label: `Departamento ${key}` });
  });
  const baseByDept = new Map();
  const departments = list.map((d) => {
    const base = buildBaseSeries(realizedByCategory(valuesByDept.get(d.key), catSet), catList, monthSources);
    baseByDept.set(d.key, base);
    return { ...d, hasBase: [...base.values()].some(hasValue) };
  });
  const base = buildBaseSeries(realizedByCategory(values, catSet), catList, monthSources);

  // ---------------- Ordem: entradas, saídas, resultado ----------------
  const compareItems = categoryItemComparator([...byCod.keys(), ...catList]);
  // Natureza do item: a do ERP; sem ela, a da maioria das filhas; sem nada, o sinal do realizado.
  const natureOfItem = (ownNature, cods) => {
    if (ownNature) return ownNature;
    const known = cods.map((cod) => leaves.get(cod).nature).filter(Boolean);
    if (known.length) return known.filter((n) => n === 'R').length * 2 > known.length ? 'R' : 'P';
    return sumSeries(base, cods) > 0 ? 'R' : 'P';
  };

  // Ordena e soma de baixo para cima (subgrupos antes das categorias do próprio nó).
  const finalize = (node) => {
    node.childIds.sort((a, b) => {
      const na = nodeById.get(a);
      const nb = nodeById.get(b);
      return compareItems({ cod: na.codDRE, label: na.descricao }, { cod: nb.codDRE, label: nb.descricao });
    });
    node.childIds.forEach((id) => finalize(nodeById.get(id)));
    node.directCats.sort((a, b) => {
      const la = leaves.get(a);
      const lb = leaves.get(b);
      if (la.direct !== lb.direct) return la.direct ? -1 : 1; // "lançamentos diretos" primeiro
      return compareItems({ cod: a, label: la.descricao }, { cod: b, label: lb.descricao });
    });
    node.allCats = [...node.childIds.flatMap((id) => nodeById.get(id).allCats), ...node.directCats];
  };
  const items = [];
  nodeById.forEach((node) => {
    if (node.parentId) return; // subgrupos entram pela linha do grupo de topo
    finalize(node);
    items.push({ id: node.id, cod: node.codDRE, label: node.descricao, cats: node.allCats, nature: natureOfItem(parseCategoryNature(byCod.get(node.codDRE)?.natureza), node.allCats) });
  });
  catList.filter((cod) => !categories.get(cod).nodeIds.length).forEach((cod) => {
    const leaf = leaves.get(cod);
    items.push({ leaf: cod, cod, label: leaf.descricao, cats: [cod], nature: natureOfItem(leaf.nature, [cod]) });
  });
  const sections = [
    { nature: 'R', nodeId: ENTRADAS_NODE_ID, label: 'Total de entradas' },
    { nature: 'P', nodeId: SAIDAS_NODE_ID, label: 'Total de saídas' },
  ]
    .map((section) => ({ ...section, items: items.filter((i) => i.nature === section.nature).sort(compareItems) }))
    .filter((section) => section.items.length);

  const totalNode = (id, descricao, allCats) => ({
    id, codDRE: id, codDRESup: null, descricao,
    position: 0, isTotalizador: true, isConta: false, isSubConta: false, isCusto: false, totalizadorAV: false,
    parentId: null, childIds: [], directCats: [], allCats, depth: 0,
  });
  sections.forEach((section) => nodeById.set(section.nodeId, totalNode(section.nodeId, section.label, section.items.flatMap((i) => i.cats))));
  nodeById.set(RESULT_NODE_ID, totalNode(RESULT_NODE_ID, 'Resultado (entradas − saídas)', [...catList]));

  // Superiores na ordem da tabela; subtotais e resultado entram no DRE calculado (statement), mas
  // fora das raízes (ajuste em massa, ranking). Subgrupos (Conta Azul, 3 níveis) entram no statement
  // logo depois do grupo de topo.
  const rootIds = sections.flatMap((section) => section.items.filter((i) => i.id).map((i) => i.id));
  const withSubgroups = (id) => [id, ...nodeById.get(id).childIds.flatMap(withSubgroups)];
  const orderedNodeIds = [...rootIds.flatMap(withSubgroups), ...sections.map((section) => section.nodeId), RESULT_NODE_ID];

  const empty = (keys) => Object.fromEntries(keys.map((k) => [k, new Set()]));
  const refEnd = String(data.refEnd || '');
  const model = {
    sync: data.sync || null,
    regime: data.regime || 'pagamento',
    refStart: data.refStart,
    refEnd: data.refEnd,
    lastClosedMonth: data.lastClosedMonth || null,
    datasetVersion: data.datasetVersion || '',
    hasConfig: Boolean(data.hasConfig) && catList.length > 0,
    planYear: Number(planYear) || (refEnd ? Number(refEnd.slice(0, 4)) + 1 : new Date().getFullYear() + 1),
    byCategory: true,
    nodeById,
    rootIds,
    orderedNodeIds,
    categories,
    catList,
    base,
    monthSources,
    // Sem o DRE não há indicadores nem "acompanha a receita"; a natureza só define o sinal sem base.
    indicatorCats: empty(INDICATOR_KEYS),
    groupCats: empty(FINANCIAL_GROUP_KEYS),
    revenueCats: new Set(sections.filter((section) => section.nature === 'R').flatMap((section) => section.items.flatMap((i) => i.cats))),
    defaultLinkedCats: new Set(),
    departments,
    baseByDept,
    sections,
  };
  model.rows = buildCategoryRows(model);
  return model;
}

/**
 * Linhas da tabela por departamento, por bloco (entradas, saídas): categoria superior (editável,
 * passa para as filhas) seguida dos subgrupos e das categorias, categorias sem superior no primeiro nível e o subtotal
 * do bloco; no fim, o resultado.
 */
export function buildCategoryRows(model) {
  const rows = [];
  const total = (nodeId) => ({
    key: `T|${nodeId}`, kind: ROW_KIND.TOTAL, nodeId,
    label: model.nodeById.get(nodeId).descricao, depth: 0, ancestors: [], hasChildren: false, editable: false,
  });
  (model.sections || []).forEach((section) => {
    section.items.forEach((item) => {
      if (item.leaf) {
        rows.push({
          key: `C|-|${item.leaf}`, kind: ROW_KIND.CATEGORY, cod: item.leaf, nodeId: null,
          label: model.categories.get(item.leaf).descricao, depth: 0, ancestors: [], hasChildren: false, editable: true,
        });
        return;
      }
      // Grupo, subgrupos (Conta Azul, 3 níveis) e as categorias de cada um.
      const pushGroup = (nodeId, depth, ancestors) => {
        const node = model.nodeById.get(nodeId);
        const groupKey = `N|${nodeId}`;
        rows.push({ key: groupKey, kind: ROW_KIND.NODE, nodeId, label: node.descricao, depth, ancestors, hasChildren: true, editable: true });
        const childAncestors = [...ancestors, groupKey];
        node.childIds.forEach((childId) => pushGroup(childId, depth + 1, childAncestors));
        node.directCats.forEach((cod) => {
          rows.push({
            key: `C|${nodeId}|${cod}`, kind: ROW_KIND.CATEGORY, cod, nodeId,
            label: model.categories.get(cod).descricao, depth: depth + 1, ancestors: childAncestors, hasChildren: false, editable: true,
          });
        });
      };
      pushGroup(item.id, 0, []);
    });
    rows.push(total(section.nodeId));
  });
  rows.push(total(RESULT_NODE_ID));
  return rows;
}

/** Metas possíveis no cenário por departamento: as categorias, na ordem da tabela. */
export const buildCategoryCatalog = (model) => (model?.rows || [])
  .filter((row) => row.kind === ROW_KIND.CATEGORY)
  .map((row) => row.cod)
  .filter((cod, i, all) => all.indexOf(cod) === i)
  .map((cod) => ({
  id: metricId(METRIC_TYPE.CATEGORIA, cod),
  type: METRIC_TYPE.CATEGORIA,
  key: cod,
  label: model.categories.get(cod)?.descricao || cod,
  format: 'currency',
  depth: model.categories.get(cod)?.nodeIds.length ? 1 : 0,
  group: 'Categorias',
}));

const deptModelCache = new WeakMap();

/** Modelo de um departamento: o da empresa com a base do departamento (memoizado). */
export const deptModel = (model, key) => {
  let byKey = deptModelCache.get(model);
  if (!byKey) {
    byKey = new Map();
    deptModelCache.set(model, byKey);
  }
  if (!byKey.has(key)) {
    const base = model.baseByDept?.get(key) || new Map(model.catList.map((cod) => [cod, zeros12()]));
    byKey.set(key, { ...model, base });
  }
  return byKey.get(key);
};

/**
 * Departamentos do plano: os do dataset e os que só existem nos ajustes salvos (departamento que saiu
 * do cadastro continua no plano, para salvar não apagar o que foi lançado).
 */
export const listDepartments = (model, state) => {
  const list = [...(model?.departments || [])];
  const known = new Set(list.map((d) => d.key));
  Object.keys(state?.byDept || {}).forEach((key) => {
    if (known.has(key)) return;
    known.add(key);
    const department = { key, label: key === GERAL_DEPT_KEY ? GERAL_DEPT_LABEL : `Departamento ${key}`, hasBase: false };
    if (key === GERAL_DEPT_KEY) list.unshift(department);
    else list.push(department);
  });
  return list;
};

export const departmentLabel = (department) =>
  department?.key === GERAL_DEPT_KEY ? `${GERAL_DEPT_LABEL} (sem departamento)` : department?.label || department?.key || '';

// ---------------- Estado ----------------

export const emptyDepartmentState = (mode) => ({ mode: normalizeInputMode(mode), linked: {}, byDept: {} });

export const startDepartmentState = (mode) => emptyDepartmentState(mode);

const emptyByMode = new Map();
const emptyFor = (mode) => {
  const m = normalizeInputMode(mode);
  if (!emptyByMode.has(m)) emptyByMode.set(m, emptyAdjustments(m));
  return emptyByMode.get(m);
};

// Estado efetivo de um departamento (ajustes próprios + chip compartilhado). Memoizado pela
// referência dos ajustes e do chip, para a simulação do departamento só refazer quando ele muda.
const effectiveCache = new WeakMap();

export const deptAdjustments = (state, key) => {
  const own = state?.byDept?.[key] || emptyFor(state?.mode);
  const linked = state?.linked || {};
  const cached = effectiveCache.get(own);
  if (cached && cached.linked === linked && cached.mode === state?.mode) return cached.value;
  const value = { ...own, mode: normalizeInputMode(state?.mode), linked };
  effectiveCache.set(own, { linked, mode: state?.mode, value });
  return value;
};

/** Aplica `updater` aos ajustes de um departamento (o chip continua no topo do estado). */
export const editDepartment = (state, key, updater) => {
  const current = deptAdjustments(state, key);
  const next = updater(current);
  if (next === current) return state;
  const { linked, mode, ...own } = next;
  const nextLinked = linked !== state.linked ? linked : state.linked;
  return { ...state, linked: nextLinked, byDept: { ...state.byDept, [key]: { ...own, mode: state.mode, linked: {} } } };
};

/** Chip "Acompanha a receita" da linha: vale para todos os departamentos. */
export const setDepartmentLinked = (state, nodeId, value) => ({ ...state, linked: { ...state.linked, [nodeId]: Boolean(value) } });

/** Linhas ajustadas somando os departamentos. */
export const countDepartmentAdjusted = (state) =>
  Object.values(state?.byDept || {}).reduce((acc, adj) => acc + countAdjusted({ ...adj, mode: state.mode }), 0);

/** Garante o formato do estado (rascunho antigo/incompleto) e alinha as chaves à grafia do DRE. */
export const normalizeDepartmentState = (model, state) => {
  const mode = normalizeInputMode(state?.mode);
  const byDept = {};
  Object.entries(state?.byDept || {}).forEach(([key, adj]) => {
    byDept[key] = alignCategoryKeys(model, { ...emptyAdjustments(mode), ...(adj || {}), mode, linked: {} });
  });
  return { mode, linked: { ...(state?.linked || {}) }, byDept };
};

// ---------------- Simulação ----------------

/** r_m da empresa: receita de todos os departamentos (base e projetada). */
export const companyRevenueVariation = (model, state) => {
  if (isValueMode(state)) return zeros12();
  const totals = { base: zeros12(), projected: zeros12() };
  listDepartments(model, state).forEach((d) => revenueTotals(deptModel(model, d.key), deptAdjustments(state, d.key), totals));
  return variationOfTotals(totals);
};

/** Bases de cada departamento (DRE e indicadores), calculadas uma vez por modelo. */
export const departmentBaseSnapshots = (model) => {
  const out = new Map();
  (model?.departments || []).forEach((d) => out.set(d.key, computeBaseSnapshot(deptModel(model, d.key))));
  return out;
};

/**
 * Simulador com memória: cada departamento só é recalculado quando os ajustes dele (ou a variação da
 * receita da empresa) mudam. Retorna { byDept: Map(depto -> simulação), consolidated, revenueVariation }.
 */
export const createDepartmentSimulator = () => {
  const cache = new Map();
  return (model, state, companyBaseSnapshot, baseSnapshots) => {
    const r = companyRevenueVariation(model, state);
    const rKey = r.join('|');
    const byDept = new Map();
    listDepartments(model, state).forEach((d) => {
      const adj = deptAdjustments(state, d.key);
      const hit = cache.get(d.key);
      if (hit && hit.model === model && hit.adj === adj && hit.rKey === rKey) {
        byDept.set(d.key, hit.simulation);
        return;
      }
      const dm = deptModel(model, d.key);
      const snapshot = baseSnapshots?.get(d.key) || computeBaseSnapshot(dm);
      const simulation = simulate(dm, adj, snapshot, { revenueVariation: r });
      cache.set(d.key, { model, adj, rKey, simulation });
      byDept.set(d.key, simulation);
    });

    const projected = new Map(model.catList.map((cod) => [cod, zeros12()]));
    byDept.forEach((simulation) => {
      simulation.catValues.forEach((series, cod) => {
        const target = projected.get(cod);
        if (!target) return;
        for (let m = 0; m < 12; m += 1) target[m] += series[m];
      });
    });
    const consolidated = finishSimulation(model, projected, r, companyBaseSnapshot || computeBaseSnapshot(model));
    return { byDept, consolidated, revenueVariation: r };
  };
};

/** Simulação avulsa (sem memória). */
export const simulateDepartments = (model, state) => createDepartmentSimulator()(model, state);

// ---------------- Ajuste em massa ----------------

/**
 * Ajuste em massa no cenário por departamento: num departamento (`department`) ou em todos (null).
 * Em todos, o R$ vale para a empresa: no pré-preenchido vira o % equivalente sobre a base consolidada
 * (o mesmo % em cada departamento); no zerado, é dividido entre os departamentos pela base do grupo.
 */
export function applyDepartmentBulk(model, state, { department = null, ...options }) {
  if (department) {
    return editDepartment(state, department, (adj) => applyBulk(deptModel(model, department), adj, options));
  }
  const keys = listDepartments(model, state).map((d) => d.key);
  const months = options.months && options.months.length ? options.months : MONTH_INDEXES;
  let perDept = () => options;

  if (options.kind === BULK_KIND.VALUE && !isValueMode(state)) {
    const companyState = { ...emptyAdjustments(state.mode), linked: state.linked || {} };
    const targets = bulkTargetNodes(model, companyState, options.group, { includeLinked: options.includeLinked });
    const pct = bulkEquivalentPct(model, targets, months, options.amount);
    if (pct == null) return state;
    perDept = () => ({ ...options, kind: BULK_KIND.PERCENT, amount: pct });
  } else if (options.kind === BULK_KIND.VALUE) {
    const weights = keys.map((key) => {
      const dm = deptModel(model, key);
      const targets = bulkTargetNodes(dm, deptAdjustments(state, key), options.group, { includeLinked: options.includeLinked });
      let total = 0;
      targets.forEach((id) => dm.nodeById.get(id).allCats.forEach((cod) => {
        months.forEach((m) => { total += Math.abs(dm.base.get(cod)?.[m] || 0); });
      }));
      return total;
    });
    const sum = weights.reduce((a, b) => a + b, 0);
    const shares = keys.map((_, i) => (sum > 0 ? weights[i] / sum : 1 / keys.length));
    perDept = (i) => ({ ...options, amount: round2((Number(options.amount) || 0) * shares[i]) });
  }

  let next = state;
  keys.forEach((key, i) => {
    next = editDepartment(next, key, (adj) => applyBulk(deptModel(model, key), adj, perDept(i)));
  });
  return next;
}

// ---------------- API ----------------

/**
 * Ajustes para a API, com o departamento de cada linha. O chip compartilhado vai em todos os
 * departamentos do plano (assim volta mesmo que só ele tenha sido mexido).
 */
export const departmentAdjustmentsToApi = (state, model) => {
  const out = [];
  listDepartments(model, state).forEach((d) => {
    adjustmentsToApi(deptAdjustments(state, d.key), deptModel(model, d.key))
      .forEach((row) => out.push({ ...row, codDepartamento: d.key }));
  });
  return out;
};

/** Reconstrói o estado por departamento a partir do cenário salvo. */
export const departmentAdjustmentsFromApi = (list, mode) => {
  const state = emptyDepartmentState(mode);
  const groups = new Map();
  (Array.isArray(list) ? list : []).forEach((row) => {
    const key = String(row?.codDepartamento ?? '').trim() || GERAL_DEPT_KEY;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  groups.forEach((rows, key) => {
    const adj = adjustmentsFromApi(rows, state.mode);
    Object.assign(state.linked, adj.linked);
    if (countAdjusted(adj) > 0) state.byDept[key] = { ...adj, linked: {} };
  });
  return state;
};

/** Estado de ajustes de um cenário salvo, no formato do tipo informado (cópia entre cenários). */
export const scenarioStateFromApi = (type, source) => {
  const mode = normalizeInputMode(source?.inputMode);
  return isDepartmentScenario(type)
    ? departmentAdjustmentsFromApi(source?.adjustments, mode)
    : adjustmentsFromApi(source?.adjustments, mode);
};

/** Projetado por departamento x categoria para a API (o backend grava também a soma). */
export const departmentProjectedValuesToApi = (departmentSimulation, state) => {
  const out = [];
  departmentSimulation.byDept.forEach((simulation, key) => {
    projectedValuesToApi(simulation, deptAdjustments(state, key))
      .forEach((row) => out.push({ ...row, codDepartamento: key }));
  });
  return out;
};

// ---------------- Metas ----------------

/**
 * Metas do cenário por departamento: uma por categoria (as de baixo; a superior é a soma delas) e
 * departamento (inclusive GERAL), para cada período da quebra escolhida. Só as combinações com valor
 * projetado no ano entram.
 */
export function buildDepartmentGoals(model, departmentSimulation, { planYear, granularity, ownerUserId = null, state }) {
  const periods = goalPeriodsFor(granularity, planYear);
  const items = [];
  listDepartments(model, state).forEach((d) => {
    const simulation = departmentSimulation?.byDept.get(d.key);
    if (!simulation) return;
    model.catList.forEach((cod) => {
      const series = simulation.catValues.get(cod);
      if (!hasValue(series)) return;
      const label = model.categories.get(cod)?.descricao || cod;
      periods.forEach((p) => {
        items.push({
          metricType: METRIC_TYPE.CATEGORIA,
          metricKey: cod,
          metricLabel: label,
          format: 'currency',
          periodStart: p.periodStart,
          periodEnd: p.periodEnd,
          value: round2(p.months.reduce((acc, m) => acc + (Number(series[m]) || 0), 0)),
          ownerUserId,
          departmentKey: d.key,
          departmentLabel: d.label,
        });
      });
    });
  });
  return items;
}

/** Departamentos que entram em "Transformar em meta" (com alguma categoria projetada). */
export const departmentsWithValues = (model, state, departmentSimulation) =>
  listDepartments(model, state).filter((d) => [...(departmentSimulation?.byDept.get(d.key)?.catValues.values() || [])].some(hasValue));
